import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Rating,
  Divider,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  LocalHospital,
  TrendingDown,
  Star
} from '@mui/icons-material';

const ProductCard = ({
  product,
  onBuyNow,
  onCompare,
  onViewDetails,
  showSavings = true,
  matchScore
}) => {
  const {
    id,
    product_name,
    product_name_ar,
    insurer_name,
    insurer_logo,
    base_premium,
    personalized_premium,
    savings,
    key_features,
    avg_review_rating,
    review_count,
    claims_approval_rate,
    avg_claim_processing_days,
    network_hospital_count
  } = product;

  const displayPremium = Number(personalized_premium ?? base_premium) || 0;
  const monthlyPremium = displayPremium > 0 ? Math.round(displayPremium / 12) : 0;

  // Parse features: API may return string (JSON), array, or object
  let features = {};
  if (key_features) {
    if (typeof key_features === 'string') {
      try {
        features = JSON.parse(key_features) || {};
      } catch {
        features = {};
      }
    } else if (Array.isArray(key_features)) {
      features = {};
    } else if (typeof key_features === 'object') {
      features = key_features;
    }
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {matchScore != null && matchScore >= 85 && (
        <Chip
          label={`${matchScore}% Match`}
          color="success"
          size="small"
          icon={<Star />}
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
        />
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {insurer_logo && (
            <Box
              component="img"
              src={insurer_logo}
              alt={insurer_name || ''}
              sx={{ width: 50, height: 50, objectFit: 'contain', mr: 2 }}
            />
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {product_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {insurer_name}
            </Typography>
          </Box>
        </Box>

        {/* Rating */}
        {avg_review_rating != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Rating value={Number(avg_review_rating)} precision={0.1} readOnly size="small" />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {Number(avg_review_rating).toFixed(1)}/5 ({review_count || 0} reviews)
            </Typography>
          </Box>
        )}

        {/* Pricing */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" color="primary">
            {displayPremium > 0 ? `$${displayPremium.toLocaleString()}/year` : '—'}
          </Typography>
          {monthlyPremium > 0 && (
            <Typography variant="body2" color="text.secondary">
              ${monthlyPremium}/month
            </Typography>
          )}
        </Box>

        {/* Savings Banner */}
        {showSavings && savings && savings.amount > 0 && (
          <Box
            sx={{
              bgcolor: 'success.light',
              color: 'success.dark',
              p: 1.5,
              borderRadius: 1,
              mb: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <TrendingDown sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                SAVE ${savings.amount}/year
              </Typography>
              <Typography variant="caption">
                vs {savings.vs_current ? 'your current plan' : 'market average'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Coverage Features */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Coverage:
        </Typography>
        <Stack spacing={0.5}>
          {features.inpatient && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
              <Typography variant="body2">Inpatient care</Typography>
            </Box>
          )}
          {features.outpatient && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
              <Typography variant="body2">Outpatient visits</Typography>
            </Box>
          )}
          {features.dental && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
              <Typography variant="body2">Dental</Typography>
            </Box>
          )}
          {features.optical && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
              <Typography variant="body2">Optical</Typography>
            </Box>
          )}
          {features.maternity && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
              <Typography variant="body2">Maternity</Typography>
            </Box>
          )}
        </Stack>

        {/* Hospital Network */}
        {network_hospital_count != null && network_hospital_count > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <LocalHospital sx={{ fontSize: 16, color: 'primary.main', mr: 1 }} />
            <Typography variant="body2">
              Covers {network_hospital_count} hospitals
            </Typography>
          </Box>
        )}

        {/* Performance Metrics */}
        {claims_approval_rate != null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              ⚡ {claims_approval_rate}% claim approval
              {avg_claim_processing_days != null && ` • ${avg_claim_processing_days}-day avg processing`}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Tutamen Perks */}
        <Box sx={{ bgcolor: 'primary.light', p: 1.5, borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary.dark" gutterBottom>
            🎁 TUTAMEN PERKS:
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontSize={12}>
              ✓ 5% cashback (${displayPremium > 0 ? Math.round(displayPremium * 0.05) : 0}/year) - Bronze tier
            </Typography>
            <Typography variant="body2" fontSize={12}>
              ✓ Upgrade to Silver: 8% cashback
            </Typography>
            <Typography variant="body2" fontSize={12}>
              ✓ 15% discount at 50+ merchants
            </Typography>
            <Typography variant="body2" fontSize={12}>
              ✓ Wellness rewards program
            </Typography>
          </Stack>
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => onBuyNow(product)}
          size="large"
        >
          Buy Now
        </Button>
        <Button variant="outlined" onClick={() => onCompare(product)}>
          Compare
        </Button>
        <Button variant="text" size="small" onClick={() => onViewDetails(product)}>
          Details
        </Button>
      </CardActions>
    </Card>
  );
};

export default ProductCard;
